import AIGeneratedLabel from "@/components/landing/blog/AIGeneratedLabel";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getBlogCategories, getBlogPostsByCategory } from "@/data/blogPosts";
import { useLanguage } from "@/hooks/useLanguage";

const BlogGrid = () => {
  const { t, localizePath, language } = useLanguage();
  const categories = getBlogCategories(language);
  const [selectedCategory, setSelectedCategory] = useState<string>(categories[0]);
  const filteredPosts = getBlogPostsByCategory(selectedCategory, language);

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12">
          <aside className="lg:w-64 shrink-0">
            <h3 className="font-semibold text-foreground mb-4">{t("blogPage.topics")}</h3>
            <nav className="space-y-1">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    selectedCategory === category
                      ? "bg-accent/10 text-accent font-medium border-l-2 border-accent"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  }`}
                >
                  {category}
                </button>
              ))}
            </nav>
          </aside>

          <div className="flex-1">
            <div className="grid md:grid-cols-2 gap-8">
              {filteredPosts.map((post) => (
                <Link key={post.slug} to={localizePath(`/blog/${post.slug}`)}>
                  <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow group border-border/50">
                    <div className="relative aspect-[16/10] bg-secondary/50 overflow-hidden">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
<AIGeneratedLabel slug={post.slug} />
                    </div>

                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <span>{post.readingTime} {t("blogPage.readingTime")}</span>
                      </div>

                      <h2 className="text-xl font-semibold text-foreground group-hover:text-accent transition-colors mb-3 line-clamp-2">
                        {post.title}
                      </h2>

                      <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-accent/10 text-accent text-xs">
                            {post.author.name.split(" ").map((n) => n[0]).join("")}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-foreground">{post.author.name}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">{t("blogPage.noArticles")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogGrid;
